import { Button, TableContainer, Text } from '@ksyos/design-system';
import { v4 as uuidv4 } from 'uuid';
import type { LabResults } from '@frontend/components/pages/customControls/utils/v1/labResults/labResults';
import type { ValidationErrors } from '@frontend/utils';
import { ProfessionalSummaryDataType } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { LabResultRecord } from '@frontend/components/pages/customControls/utils/v1/labResults/labResults';
import { LabResultsEntryAddEdit } from '@frontend/components/pages/customControls/utils/v1/labResults/LabResultsEntryAddEdit';
import { ProfessionalSummaryDataAction } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataSection';

type LabResultsCanAddRenderProps = {
    errors?: ValidationErrors;
    labResults: LabResults | undefined;
    setLabResults: React.Dispatch<React.SetStateAction<LabResults | undefined>>;
    setValidationKeys: React.Dispatch<React.SetStateAction<string[]>>;
    validationKeys: string[];
};

export function LabResultsCanAdd(props: Readonly<LabResultsCanAddRenderProps>) {
    const { errors, labResults, setLabResults, setValidationKeys, validationKeys } = props;
    const translate = getLanguage();

    return (
        <>
            <Text
                fontWeight="medium"
                size="sm"
                sx={{ mb: 2 }}
            >
                {translate.professionalSummary.diagnosticDeterminationsRead}
            </Text>
            {labResults && labResults?.size > 0 ? <TableContainer>{renderLabResults()}</TableContainer> : null}
            <Button
                iconLeft="add"
                onClick={addEmptyLabResult}
                variant="link"
            >
                Voeg extra {translate.professionalSummary[ProfessionalSummaryDataType.diagnosticDeterminations]} toe
            </Button>
        </>
    );

    function renderLabResults() {
        if (!labResults) {
            return;
        }

        const labResultsRows = [];
        let counter = 0;
        for (const [key, labResult] of labResults.entrySeq().toArray() as [string, LabResultRecord][]) {
            labResultsRows.push(
                <LabResultsEntryAddEdit
                    errorKey={counter++}
                    errors={errors}
                    key={key}
                    labResultEntry={labResult}
                    labResultEntryId={key}
                    onChange={handleChangeLabResults}
                    onDelete={handleDeleteLabResults}
                />
            );
        }

        return labResultsRows;
    }

    function addEmptyLabResult() {
        setLabResults(
            labResults?.set(
                uuidv4(),
                new LabResultRecord({
                    action: ProfessionalSummaryDataAction.pick,
                    codeDisplayName: '',
                    id: uuidv4(),
                    startDate: null,
                    valueUnit: '',
                    valueValue: '',
                })
            )
        );
    }

    async function handleChangeLabResults(
        LabResult: LabResultRecord,
        labResultEntryKey: string,
        keysToBeValidated: string[]
    ) {
        setLabResults(labResults?.set(labResultEntryKey, LabResult));
        if (keysToBeValidated.length > 0 && !keysToBeValidated.some((key) => validationKeys.includes(key))) {
            setValidationKeys([...validationKeys, ...keysToBeValidated]);
        }
    }

    async function handleDeleteLabResults(labResultEntryKey: string) {
        setLabResults(labResults?.delete(labResultEntryKey));
    }
}
